import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MAX_CHAIN = 2;
const ignorable = (e) => e.kind === "console" || /^script error\.?$/i.test((e.message || "").trim());

export const formatErrors = (errors) => errors
  .map((e) => `[${e.kind}] ${e.message}${e.source ? ` (${e.source}${e.line ? `:${e.line}` : ""})` : ""}${e.page ? ` on ${e.page}` : ""}`)
  .join("\n").slice(0, 3000);

// Consecutive auto-fix requests since the user last wrote something themselves.
const autoFixChain = (messages) => {
  let n = 0;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.role !== "user") continue;
    if (m.kind !== "autofix") break;
    n += 1;
  }
  return n;
};

export function useAutoFix({ version, viewing, building, messages, enabled, onFix }) {
  const [errors, setErrors] = useState([]);
  const tried = useRef(new Set());
  const fixable = useMemo(() => errors.filter((e) => !ignorable(e)), [errors]);
  const chain = autoFixChain(messages);
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const stoppedByUser = messages[messages.length - 1]?.status === "stopped";
  const versionId = version?.id;

  const addError = useCallback((e) => setErrors((xs) => (xs.length >= 12 || xs.some((x) => x.message === e.message) ? xs : [...xs, e])), []);
  const reset = useCallback(() => setErrors([]), []);
  const fixNow = useCallback(() => {
    if (!fixable.length && !errors.length) return;
    tried.current.add(versionId);
    onFix(formatErrors(fixable.length ? fixable : errors));
  }, [fixable, errors, versionId, onFix]);

  const fixRef = useRef(fixNow);
  fixRef.current = fixNow;

  const auto = enabled && !building && !viewing && !stoppedByUser && !!versionId && fixable.length > 0 && chain < MAX_CHAIN && !tried.current.has(versionId);

  useEffect(() => {
    if (!auto) return undefined;
    const t = setTimeout(() => fixRef.current(), 2500);
    return () => clearTimeout(t);
  }, [auto]);

  let status = "idle";
  if (auto) status = "pending";
  else if (building && lastUser?.kind === "autofix") status = "fixing";
  else if (enabled && fixable.length && chain >= MAX_CHAIN && !building) status = "gaveup";

  return { errors: viewing ? [] : errors, addError, reset, fixNow, status };
}
